import React from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, LinkSlashIcon } from '@heroicons/react/24/outline';

/**
 * Dashboard / page banner when stores are disconnected or current store lost access.
 */
export default function StoreConnectionBanner({
  summary,
  disconnectedStores = [],
  currentStore,
  isDarkMode = false,
}) {
  const currentDisconnected = currentStore && !currentStore.isConnected;
  const disconnectedCount = summary?.disconnectedCount ?? disconnectedStores.length;

  if (!currentDisconnected && disconnectedCount === 0) {
    return null;
  }

  const shell = isDarkMode
    ? 'bg-amber-950/40 border-amber-800/60 text-amber-100'
    : 'bg-amber-50 border-amber-300 text-amber-950';

  if (currentDisconnected) {
    return (
      <div className={`rounded-lg border-l-4 border-l-amber-500 p-4 mb-4 ${shell}`}>
        <div className="flex items-start gap-3">
          <LinkSlashIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">
              {currentStore.name} — Amazon access removed
            </p>
            <p className="text-sm mt-1 opacity-90">
              {currentStore.connectionStatusLabel || 'This store is disconnected.'}
              {' '}You can still browse cached campaigns and metrics from the last sync. Reconnect to pull fresh data from Amazon.
            </p>
            <Link
              to="/stores"
              className="inline-block mt-2 text-sm font-medium text-amber-700 hover:underline"
            >
              Go to stores to reconnect →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const preview = disconnectedStores.slice(0, 3).map((s) => s.storeName || s.name).join(', ');
  const extra = disconnectedCount > 3 ? ` +${disconnectedCount - 3} more` : '';

  return (
    <div className={`rounded-lg border-l-4 border-l-amber-500 p-4 mb-4 ${shell}`}>
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">
            {disconnectedCount} store{disconnectedCount !== 1 ? 's' : ''} disconnected
          </p>
          <p className="text-sm mt-1 opacity-90 truncate">
            {preview}{extra}
          </p>
          <Link
            to="/stores"
            className="inline-block mt-2 text-sm font-medium text-amber-700 hover:underline"
          >
            View connected & disconnected stores →
          </Link>
        </div>
      </div>
    </div>
  );
}
